import { filterTypesEnum, type FilterOption } from "@/types/common/filters";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  TableData,
  useToast,
  Avatar,
  AvatarFallback,
} from "@elaniintc/goes-design-system";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { parseAsString, useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import TableHeader from "@/components/common/table/header";
import Pagination from "@/components/common/table/pagination";
import {
  useGetAnnouncements,
  useDeleteAnnouncement,
  useToggleAnnouncementStatus,
} from "@/queries/announcements";
import type { AnnouncementType } from "@/types/entities/announcements";
import NewNotificationModal from "@/components/portal/notifications/new-notification-modal";
import ConfirmationModalBase from "@/components/common/modals/confirmation-modal-base";
import { TableLeadingCell } from "@/components/ui/table-leading-cell";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";

// Map API status to display status
const mapStatusToDisplay = (
  status: string,
): "Borrador" | "Publicada" | "Despublicada" | "Archivada" => {
  switch (status) {
    case "draft":
      return "Borrador";
    case "published":
      return "Publicada";
    case "unpublished":
      return "Despublicada";
    case "archived":
      return "Archivada";
    default:
      return "Borrador";
  }
};

// Map display status to API status
const mapDisplayToApiStatus = (
  status: string,
): "draft" | "published" | "unpublished" | "archived" => {
  switch (status) {
    case "Borrador":
      return "draft";
    case "Publicada":
      return "published";
    case "Despublicada":
      return "unpublished";
    case "Archivada":
      return "archived";
    default:
      return "draft";
  }
};

const columnHelper = createColumnHelper<AnnouncementType>();

const Menu = ({
  notification,
  onEdit,
  onToggleStatus,
  onDelete,
}: {
  notification: AnnouncementType;
  onEdit: (notification: AnnouncementType) => void;
  onToggleStatus: (notification: AnnouncementType) => void;
  onDelete: (notification: AnnouncementType) => void;
}) => {
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions.announcements.update);
  const canDelete = hasPermission(Permissions.announcements.delete);

  if (!canEdit && !canDelete) return null;

  const isPublished = notification.status === "published";
  const isArchived = notification.status === "archived";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" className="size-8">
          <Icon
            type="far"
            name="ellipsis-vertical"
            className="text-current text-sm"
          />
          <span className="sr-only">Acciones para notificación</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[222px] min-w-56 rounded-lg border-neutral-silent text-body font-normal p-2"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          {/* Opción de editar - siempre disponible */}
          {canEdit && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={() => onEdit(notification)}
            >
              Editar notificación
            </DropdownMenuItem>
          )}

          {isArchived && (
            <DropdownMenuItem
              className="py-2 px-3 text-body-quiet cursor-not-allowed opacity-50"
              disabled
            >
              Despublicar notificación
            </DropdownMenuItem>
          )}

          {!isArchived && (
            <>
              {isPublished && (
                <DropdownMenuItem
                  className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                  onClick={() => onToggleStatus(notification)}
                >
                  Despublicar notificación
                </DropdownMenuItem>
              )}
              {!isPublished && (
                <DropdownMenuItem
                  className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                  onClick={() => onToggleStatus(notification)}
                >
                  Publicar notificación
                </DropdownMenuItem>
              )}
            </>
          )}

          {/* La opción de eliminar siempre está disponible */}
          {canDelete && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 text-danger cursor-pointer"
              onClick={() => onDelete(notification)}
            >
              Eliminar notificación
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NotificationsPage = () => {
  const { hasPermission } = useAuthorization();
  const canCreate = hasPermission(Permissions.announcements.create);
  const canEdit = hasPermission(Permissions.announcements.update);
  const canDelete = hasPermission(Permissions.announcements.delete);

  const [isNewNotificationModalOpen, setIsNewNotificationModalOpen] =
    useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<AnnouncementType | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isToggleStatusModalOpen, setIsToggleStatusModalOpen] = useState(false);

  const { showToast } = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);

  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useQueryState("search", parseAsString);
  const [published_at, setPublishedAt] = useQueryState(
    "published_at",
    parseAsString,
  );
  const [status, setStatus] = useQueryState("status", parseAsString);
  const [order_by, setOrderBy] = useQueryState(
    "order_by",
    parseAsString.withDefault("!created_at"),
  );
  const [searchInput, setSearchInput] = useState<string | null>(query || "");
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);
  const [modalState, setModalState] = useState<"idle" | "filters">("idle");

  // Función para inicializar filtros basados en la URL
  const initializeFilters = useCallback(() => {
    const initialFilters: FilterOption[] = [
      {
        id: 1,
        field: "published_at",
        type: filterTypesEnum.dates,
        label: "Fecha de Publicación",
        isUpdated: !!published_at,
        shouldDisabledFutureDates: true,
        result: published_at || "",
      },
      {
        id: 2,
        field: "status",
        type: filterTypesEnum.status,
        label: "Estado de la notificación",
        isUpdated: !!status,
        result: status || "",
        status: [
          {
            field: "Borrador",
            label: "Borrador",
            value: status?.includes("Borrador") || false,
          },
          {
            field: "Publicada",
            label: "Publicada",
            value: status?.includes("Publicada") || false,
          },
          {
            field: "Archivada",
            label: "Archivada",
            value: status?.includes("Archivada") || false,
          },
        ],
      },
    ];
    return initialFilters;
  }, [published_at, status]);

  const [formatFilters, setFormatFilters] = useState<FilterOption[]>([]);

  // Inicializar filtros cuando el componente se monta o cambian los parámetros de URL
  useEffect(() => {
    const filters = initializeFilters();
    setFormatFilters(filters);
  }, [initializeFilters]);

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  // Get announcements from API
  const apiParams = {
    page,
    per_page: 20,
    ...(deferredQuery ? { search: deferredQuery } : {}),
    ...(status
      ? {
          status: status.includes(",")
            ? status
                .split(",")
                .map((s) => mapDisplayToApiStatus(s.trim()))
                .join(",")
            : mapDisplayToApiStatus(status),
        }
      : {}),
    ...(published_at ? { date_range: published_at } : {}),
    ...(order_by
      ? {
          sort_by: order_by.startsWith("!")
            ? (order_by.slice(1) as
                | "created_at"
                | "title"
                | "content"
                | "status"
                | "is_published"
                | "updated_at")
            : (order_by as
                | "created_at"
                | "title"
                | "content"
                | "status"
                | "is_published"
                | "updated_at"),
          sort_direction: (order_by.startsWith("!") ? "desc" : "asc") as
            | "asc"
            | "desc",
        }
      : {}),
  };

  const { data, isFetching } = useGetAnnouncements(apiParams);

  const shouldShowSkeleton = !data;

  // Mutations
  const deleteMutation = useDeleteAnnouncement();
  const toggleStatusMutation = useToggleAnnouncementStatus();

  // Action handlers
  const handleEditNotification = useCallback(
    (notification: AnnouncementType) => {
      setSelectedNotification(notification);
      setIsEditModalOpen(true);
    },
    [],
  );

  const handleDeleteNotification = useCallback(
    (notification: AnnouncementType) => {
      setSelectedNotification(notification);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const handleToggleStatus = useCallback((notification: AnnouncementType) => {
    setSelectedNotification(notification);
    setIsToggleStatusModalOpen(true);
  }, []);

  const handleCellButtonClick = useCallback(
    (notification: AnnouncementType) => {
      setSelectedNotification(notification);
      setIsEditModalOpen(true);
    },
    [],
  );

  const confirmDelete = () => {
    if (selectedNotification) {
      deleteMutation.mutate(selectedNotification.id, {
        onSuccess: () => {
          showToast({
            title: "Notificación eliminada",
            description: "La notificación fue eliminada exitosamente.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square" type={"ghost"}>
                    <Icon type="fas" name="bell-slash" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
          setIsDeleteModalOpen(false);
          setSelectedNotification(null);
          // Cerrar el modal de edición si está abierto
          if (isEditModalOpen) {
            setIsEditModalOpen(false);
          }
        },
        onError: () => {
          showToast({
            title: "Error al eliminar",
            description:
              "La notificación no pudo ser eliminada. Por favor inténtalo nuevamente.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square">
                    <Icon type="fas" name="exclamation-triangle" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
        },
      });
    }
  };

  const confirmToggleStatus = () => {
    if (selectedNotification) {
      const action =
        selectedNotification.status === "published" ? "unpublish" : "publish";
      const isPublishing = action === "publish";

      toggleStatusMutation.mutate(
        {
          id: selectedNotification.id,
          action,
        },
        {
          onSuccess: () => {
            if (isPublishing) {
              showToast({
                title: "Notificación publicada",
                description: "La notificación fue publicada exitosamente.",
                options: {
                  type: "success",
                  duration: 4000,
                  customIcon: (
                    <Avatar size="xs" shape="square">
                      <AvatarFallback color="success" shape="square">
                        <Icon type="fas" name="bell-ring" />
                      </AvatarFallback>
                    </Avatar>
                  ),
                },
              });
            } else {
              showToast({
                title: "Notificación despublicada",
                description: "La notificación fue despublicada exitosamente.",
                options: {
                  type: "success",
                  duration: 4000,
                  customIcon: (
                    <Avatar size="xs" shape="square">
                      <AvatarFallback color="warning" shape="square">
                        <Icon type="fas" name="bell-slash" />
                      </AvatarFallback>
                    </Avatar>
                  ),
                },
              });
            }
            setIsToggleStatusModalOpen(false);
            setSelectedNotification(null);
          },
          onError: () => {
            showToast({
              title: isPublishing
                ? "Error al publicar"
                : "Error al despublicar",
              description: `Los cambios en las notificaciones no pudieron ser ${isPublishing ? "publicados" : "despublicados"}. Por favor inténtalo de nuevo.`,
              options: {
                type: "error",
                duration: 4000,
                customIcon: (
                  <Avatar size="xs" shape="square">
                    <AvatarFallback color="danger" shape="square">
                      <Icon type="fas" name="exclamation-triangle" />
                    </AvatarFallback>
                  </Avatar>
                ),
              },
            });
          },
        },
      );
    }
  };

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("title", {
        id: "title",
        header: "TÍTULO & DESCRIPCIÓN",
        meta: {
          className: "w-[350px] min-w-[300px] max-w-[350px]",
          cellClassName: "group",
        },
        cell: (info) => {
          const { title, content } = info.row.original;
          return (
            <TableLeadingCell
              title={title}
              subtitle={content}
              onButtonClick={() => handleCellButtonClick(info.row.original)}
            />
          );
        },
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: "ESTATUS",
        meta: {
          className: "hidden md:table-cell",
        },
        cell: (info) => {
          const status = mapStatusToDisplay(info.getValue());
          if (status === "Publicada") {
            return (
              <Badge variant="success" shape="pill" size="sm">
                Publicada
              </Badge>
            );
          }
          if (status === "Borrador") {
            return (
              <Badge variant="action" shape="pill" size="sm">
                Borrador
              </Badge>
            );
          }
          if (status === "Despublicada") {
            return (
              <Badge variant="warning" shape="pill" size="sm">
                Despublicada
              </Badge>
            );
          }
          if (status === "Archivada") {
            return (
              <Badge variant="neutral" shape="pill" size="sm">
                Archivada
              </Badge>
            );
          }
          return "-";
        },
      }),
      columnHelper.accessor("updated_at", {
        id: "updated_at",
        header: "ÚLTIMA ACTUALIZACIÓN",
        meta: {
          className: "hidden sm:table-cell",
        },
        cell: (info) => {
          const date = info.getValue();
          return date ? (
            <div className="text-body text-base font-normal leading-7">
              {dayjs(date).format("MMM DD, YYYY | HH:mm A")}
            </div>
          ) : (
            "-"
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        meta: {
          className: "w-[56px]",
        },
        cell: (info) => {
          return (
            <div className="w-full flex justify-end">
              <Menu
                notification={info.row.original}
                onEdit={handleEditNotification}
                onToggleStatus={handleToggleStatus}
                onDelete={handleDeleteNotification}
              />
            </div>
          );
        },
      }),
    ].filter(({ id }) => {
      if (!canEdit && !canDelete) return id !== "actions";
      return true;
    });
  }, [
    canEdit,
    canDelete,
    handleEditNotification,
    handleToggleStatus,
    handleDeleteNotification,
    handleCellButtonClick,
  ]);

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "published_at") {
          setPublishedAt(f.result || null);
        }
        if (f.field === "status") {
          // Manejar múltiples estados separados por comas
          setStatus(f.result || null);
        }
        setPage(1);
      }
    });

    // Set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters([
      {
        id: 1,
        field: "published_at",
        type: filterTypesEnum.dates,
        label: "Fecha de Publicación",
        shouldDisabledFutureDates: true,
        isUpdated: false,
        result: "",
      },
      {
        id: 2,
        field: "status",
        type: filterTypesEnum.status,
        label: "Estado de la notificación",
        isUpdated: false,
        result: "",
        status: [
          {
            field: "Borrador",
            label: "Borrador",
            value: false,
          },
          {
            field: "Publicada",
            label: "Publicada",
            value: false,
          },
          {
            field: "Archivada",
            label: "Archivada",
            value: false,
          },
        ],
      },
    ]);
    setPublishedAt(null);
    setStatus(null);
    setModalState("idle");
  };

  return (
    <>
      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            const filters = onApplyFilters(newFilters);
            setFormatFilters(filters);
            setModalState("idle");
          }}
        />
      )}
      {isNewNotificationModalOpen && (
        <NewNotificationModal
          isOpen={isNewNotificationModalOpen}
          onClose={() => setIsNewNotificationModalOpen(false)}
        />
      )}

      <div className="h-full flex flex-col gap-3">
        <p className="font-normal text-body leading-7 text-base">
          Gestiona las notificaciones que se envían a los usuarios del portal de
          esta institución.
        </p>
        <div className="h-full min-h-[500px] relative">
          <div className="h-full flex flex-col bg-neutral-object border-neutral border-[0.5px] rounded-xl shadow-neutral-md overflow-hidden">
            <TableHeader
              createLabel="Nueva notificación"
              search={searchInput}
              setSearch={setSearchInput}
              title="Lista de notificaciones"
              isLoading={isFetching}
              canCreate={canCreate}
              onCreate={() => setIsNewNotificationModalOpen(true)}
              onFilter={() => setModalState("filters")}
              filtersApplied={formatFilters.filter((f) => f.isUpdated).length}
            />
            <TableData
              wrapperClassName="min-h-0 flex-1 overflow-auto h-full relative"
              headerClassName="sticky top-0 bg-neutral-object z-20"
              columns={columns as ColumnDef<AnnouncementType>[]}
              data={data?.data || []}
              sorting={sorting}
              onSortingChange={(val: SortingState) => {
                setSorting(val);
                if (val.length === 0) {
                  setOrderBy("!created_at");
                  return;
                }
                setOrderBy(
                  val.length > 0
                    ? `${val[0]?.desc ? "!" : ""}${val[0].id}`
                    : "!created_at",
                );
              }}
              isFirstLoading={shouldShowSkeleton}
              stripped
            />
            <div className="p-3">
              {data?.meta && (
                <Pagination
                  currentPage={data.meta.current_page}
                  totalItems={data.meta.total}
                  hasNext={data.meta.current_page < data.meta.last_page}
                  hasPrevious={data.meta.current_page > 1}
                  onNext={() =>
                    setPage(Math.min(page + 1, data.meta.last_page))
                  }
                  onPageChange={setPage}
                  onPrevious={() => setPage(Math.max(page - 1, 1))}
                  pageSize={20}
                  totalPages={data.meta.last_page}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmación para eliminar */}
      <ConfirmationModalBase
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onAccept={confirmDelete}
        title="Eliminar notificación"
        description={`¿Estás seguro de que deseas eliminar la notificación "${selectedNotification?.title}"? Esta acción no se puede deshacer.`}
        acceptText="Eliminar"
        cancelText="Cancelar"
        acceptVariant="danger-primary"
        isLoading={deleteMutation.isPending}
      />

      {/* Modal de confirmación para cambiar estatus */}
      <ConfirmationModalBase
        isOpen={isToggleStatusModalOpen}
        onClose={() => setIsToggleStatusModalOpen(false)}
        onAccept={confirmToggleStatus}
        title={
          selectedNotification?.status === "published"
            ? "Despublicar notificación"
            : "Publicar notificación"
        }
        description={`¿Estás seguro de que deseas ${selectedNotification?.status === "published" ? "despublicar" : "publicar"} la notificación "${selectedNotification?.title}"?`}
        acceptText={
          selectedNotification?.status === "published"
            ? "Despublicar"
            : "Publicar"
        }
        cancelText="Cancelar"
        acceptVariant="default"
        isLoading={toggleStatusMutation.isPending}
      />

      {/* Modal de edición - Pasamos la notificación seleccionada */}
      {isEditModalOpen && selectedNotification && (
        <NewNotificationModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedNotification(null);
          }}
          notification={selectedNotification}
          onDelete={handleDeleteNotification}
        />
      )}
    </>
  );
};

export default NotificationsPage;
